import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CakeCard from '../components/CakeCard';
import { fetchCakes, fetchCategories, getCachedCakes, getCachedCategories } from '../data/cakes';
import './CategoryPage.css';

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [cakes, setCakes] = useState([]);
  const [category, setCategory] = useState(null);
  const [activeSubcategory, setActiveSubcategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [sortOrder, setSortOrder] = useState('default');

  useEffect(() => {
    loadData();
  }, [categoryId]);

  const processData = (allCakes, allCategories) => {
      setCategories(allCategories);

      // Find current category (it could be a parent or a subcategory)
      let currentCategory = allCategories.find(c => c.id.toString() === categoryId);
      let isSubcategory = false;
      let parentCategory = null;

      // If not found in top level, check subcategories
      if (!currentCategory) {
        for (const cat of allCategories) {
          if (cat.subcategories) {
            const sub = cat.subcategories.find(s => s.id.toString() === categoryId);
            if (sub) {
              currentCategory = sub;
              isSubcategory = true;
              parentCategory = cat;
              break;
            }
          }
        }
      }
      
      const targetCategory = isSubcategory ? parentCategory : currentCategory;
      setCategory(targetCategory);

      if (!targetCategory) return; // Should handle not found state

      // Filter cakes for this category (always get ALL cakes for the parent to calculate counts)
      let filtered = allCakes.filter(cake => {
        if (targetCategory) {
             const subIds = targetCategory.subcategories ? targetCategory.subcategories.map(s => s.id) : [];
             // Check if cake matches parent ID or any subcategory ID
             // Note: cake.category_id might need loose comparison
             return cake.category_id == targetCategory.id || subIds.some(sid => sid == cake.category_id);
        }
        return false;
      });

      setCakes(filtered);
      
      // Only set active subcategory if it's the first load or explicit subcategory navigation
      // We are careful not to reset user selection if we were re-processing data, but since this runs on mount/id change, it's fine.
      if (isSubcategory) {
          setActiveSubcategory(categoryId);
      } else {
          setActiveSubcategory('all');
      }
  };

  const loadData = async () => {
    // 1. Try to load from cache first
    const cachedCakes = getCachedCakes();
    const cachedCategories = getCachedCategories();

    if (cachedCakes && cachedCategories) {
        processData(cachedCakes, cachedCategories);
        setLoading(false);
    } else {
        setLoading(true);
    }

    try {
      // 2. Fetch fresh data
      const [allCakes, allCategories] = await Promise.all([
        fetchCakes(),
        fetchCategories()
      ]);
      
      // Update with fresh data
      processData(allCakes, allCategories);
      
    } catch (error) {
      console.error('Error loading category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubcategoryClick = (id) => {
    setActiveSubcategory(id);
  };
  
  const handleAddToCart = (cake) => {
    navigate(`/cake/${cake.id}`);
  };

  if (loading) {
    return (
      <div className="category-page">
         <Header title="Loading..." />
         <div className="container" style={{paddingTop: '2rem'}}>
             <div className="loading-grid">
               {[1, 2, 3, 4].map(i => (
                 <div key={i} className="skeleton-card" style={{height: '250px'}}></div>
               ))}
             </div>
         </div>
      </div>
    );
  }

  if (!category) {
    return (
        <div className="category-page">
            <Header title="Category Not Found" />
            <div className="container empty-state">
                <p>We couldn't find the category you're looking for.</p>
                <button className="btn-primary" onClick={() => navigate('/')}>Go Home</button>
            </div>
        </div>
    );
  }

  // Filter displayed cakes based on active subcategory and search term
  const filteredCakes = cakes.filter(cake => {
      // 1. Subcategory Filter
      if (activeSubcategory !== 'all' && cake.category_id.toString() !== activeSubcategory.toString()) {
          return false;
      }
      // 2. Search Filter
      if (searchTerm && !cake.title.toLowerCase().includes(searchTerm.toLowerCase())) {
          return false;
      }
      return true;
  });

  const displayedCakes = [...filteredCakes].sort((a, b) => {
    if (sortOrder === 'default') return 0;
    const priceA = (a.base_price || 0) + (a.custom_design_fee || 0);
    const priceB = (b.base_price || 0) + (b.custom_design_fee || 0);
    return sortOrder === 'asc' ? priceA - priceB : priceB - priceA;
  });

  return (
    <div className="category-page">
      <Header 
        title={category.name} 
        subtitle={`${displayedCakes.length} Cakes`} 
        showBack={true} 
      />

      <div className="container">
        {/* Search Bar */}
        <div className="search-section" style={{ padding: '0 0 1rem 0' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder={`Search in ${category.name}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 20px',
                paddingLeft: '45px',
                borderRadius: '30px',
                border: '1px solid #ddd',
                fontSize: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
            />
            <svg
              style={{
                position: 'absolute',
                left: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9c6b5f'
              }}
              width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                ×
              </button>
            )}
          </div>
          
          <div style={{ marginTop: '0.8rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 5px' }}>
            <span style={{ fontSize: '0.9rem', color: '#666', visibility: 'hidden' }}>
               {displayedCakes.length} Results
            </span>
            <div style={{ position: 'relative' }}>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                style={{
                  appearance: 'none',
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '20px',
                  padding: '8px 32px 8px 16px',
                  fontSize: '13px',
                  color: '#555',
                  cursor: 'pointer',
                  outline: 'none',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
              >
                <option value="default">Sort by: Relevancy</option>
                <option value="asc">Price: Low to High</option>
                <option value="desc">Price: High to Low</option>
              </select>
              <svg 
                style={{ 
                  position: 'absolute', 
                  right: '10px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#999'
                }} 
                width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
        </div>

        {/* Subcategories Bubbles */}
        {category.subcategories && category.subcategories.length > 0 && (
          <section className="categories-section" style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
            <div className="categories-scroll">
              <button
                className={`category-bubble-btn ${activeSubcategory === 'all' ? 'active' : ''}`}
                onClick={() => handleSubcategoryClick('all')}
              >
                <div className={`bubble-img-container no-image`}>
                  <span className="fallback-text">All</span>
                </div>
                <span className="bubble-label">All ({cakes.length})</span>
              </button>
              {category.subcategories.map(sub => {
                const count = cakes.filter(c => c.category_id == sub.id).length;
                return (
                  <button
                    key={sub.id}
                    className={`category-bubble-btn ${activeSubcategory.toString() === sub.id.toString() ? 'active' : ''}`}
                    onClick={() => handleSubcategoryClick(sub.id)}
                  >
                    <div className={`bubble-img-container ${!sub.image ? 'no-image' : ''}`}>
                      {sub.image ? (
                        <img 
                          src={`https://panel.supplebakes.com/${sub.image}`} 
                          alt={sub.name}
                          className="bubble-img"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentNode.classList.add('image-error');
                          }}
                        />
                      ) : (
                        <span className="fallback-text">{sub.name.charAt(0)}</span>
                      )}
                    </div>
                    <span className="bubble-label">{sub.name} ({count})</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Products Grid */}
        <div className="products-section">
            {displayedCakes.length > 0 ? (
                <div className="cakes-grid">
                {displayedCakes.map(cake => (
                    <CakeCard 
                    key={cake.id} 
                    cake={cake} 
                    onAddToCart={handleAddToCart}
                    />
                ))}
                </div>
            ) : (
                <div className="empty-state">
                    <h3>{searchTerm ? `No results for "${searchTerm}"` : 'No cakes found'}</h3>
                </div>
            )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CategoryPage;
